import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Bell, Plus, Search, MoreVertical, Pencil, Trash2, Check,
  AlertTriangle, Calendar as CalendarIcon, Sunrise, Repeat, X,
} from 'lucide-react';
import { safeTime } from '../lib/formatters';

const API = process.env.REACT_APP_BACKEND_URL;
const AR_TZ = 'America/Argentina/Buenos_Aires';

// ---------- helpers ----------
// IMPORTANTE: para las HORAS y DÍAS mostrados usamos EXACTAMENTE los mismos
// formatters que CalendarView (safeTime / toLocaleString browser-local) para
// garantizar paridad 1:1. Sólo el BUCKETING (vencidos/hoy/mañana) usa AR TZ,
// porque semánticamente NEO guardó la fecha en AR.
const AR_DAYS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const AR_MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

const parseAR = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
};

// Componentes calendario en AR TZ (para bucketing y comparaciones semánticas)
const arDayKey = (d) => {
  return d.toLocaleDateString('en-CA', { timeZone: AR_TZ });
};

const daysBetweenAR = (aIso, bDate) => {
  const a = arDayKey(aIso);
  const b = arDayKey(bDate);
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  const A = Date.UTC(ay, am - 1, ad);
  const B = Date.UTC(by, bm - 1, bd);
  return Math.round((A - B) / 86400000);
};

// Mostrar día/mes en el mismo TZ que Calendar (browser-local, sin forzar AR).
const humanDate = (d) => `${AR_DAYS[d.getDay()]} ${d.getDate()} ${AR_MONTHS[d.getMonth()]}`;
// Hora exactamente igual que CalendarView: safeTime(iso).
const humanTime = (iso) => safeTime(iso, { hour: '2-digit', minute: '2-digit', hour12: false }, null);

const bucketFor = (remindAt, now) => {
  const d = parseAR(remindAt);
  if (!d) return 'proximos';
  const diff = daysBetweenAR(d, now);
  if (diff < 0) return 'vencidos';
  if (diff === 0) return 'hoy';
  if (diff === 1) return 'manana';
  return 'proximos';
};

const toDateTimeLocal = (iso) => {
  const d = parseAR(iso);
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// ---------- subcomponents ----------
function FilterChip({ label, count, active, onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={[
        'flex-shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-all border',
        active
          ? 'bg-[#2563EB] text-white border-[#2563EB] shadow-md shadow-[#2563EB]/25'
          : 'bg-white dark:bg-white/5 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-white/10 hover:border-[#2563EB]/40 hover:text-[#2563EB] dark:hover:text-[#3B82F6]',
      ].join(' ')}
    >
      {label}
      {typeof count === 'number' && (
        <span className={[
          'ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold',
          active ? 'bg-white/25 text-white' : 'bg-slate-100 dark:bg-white/10 text-slate-500 dark:text-slate-400',
        ].join(' ')}>
          {count}
        </span>
      )}
    </button>
  );
}

function ReminderCard({ reminder, onComplete, onEdit, onDelete, isOverdue }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [completing, setCompleting] = useState(false);
  const d = parseAR(reminder.remind_at);
  const time = humanTime(reminder.remind_at);
  const dateLabel = d ? humanDate(d) : '—';
  const isRecurring = !!reminder.recurrence;
  const recLabel = reminder.recurrence_label || (isRecurring ? 'Se repite' : null);

  const handleComplete = () => {
    setCompleting(true);
    setTimeout(() => onComplete(reminder), 220);
  };

  return (
    <div
      data-testid={`reminder-card-${reminder.id}`}
      className={[
        'group relative flex items-start gap-3 p-4 rounded-2xl bg-white dark:bg-[#0B1220] border transition-all duration-200',
        isOverdue
          ? 'border-red-200/70 dark:border-red-500/25'
          : 'border-slate-200/70 dark:border-white/5 hover:border-[#2563EB]/40 dark:hover:border-[#3B82F6]/40',
        'shadow-sm hover:shadow-md',
        completing ? 'opacity-0 scale-[0.98] translate-x-2' : 'opacity-100',
      ].join(' ')}
    >
      {/* Checkbox */}
      <button
        data-testid={`reminder-complete-${reminder.id}`}
        onClick={handleComplete}
        aria-label="Marcar como hecho"
        className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-full border-2 border-slate-300 dark:border-white/20 hover:border-[#2563EB] hover:bg-[#2563EB]/5 transition-colors flex items-center justify-center group/check"
      >
        <Check className="w-3.5 h-3.5 text-[#2563EB] opacity-0 group-hover/check:opacity-100 transition-opacity" strokeWidth={3} />
      </button>

      {/* Icon tile */}
      <div className={[
        'flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center',
        isOverdue
          ? 'bg-red-50 dark:bg-red-500/10 text-red-500 dark:text-red-400'
          : 'bg-[#2563EB]/10 dark:bg-[#3B82F6]/15 text-[#2563EB] dark:text-[#3B82F6]',
      ].join(' ')}>
        <Bell className="w-5 h-5" strokeWidth={2} />
      </div>

      {/* Body */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-[15px] font-bold text-slate-900 dark:text-white leading-tight truncate">
            {reminder.title}
          </h3>
          {isRecurring && (
            <span
              data-testid={`reminder-recurring-${reminder.id}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-100 dark:bg-violet-500/15 text-violet-700 dark:text-violet-300"
            >
              <Repeat className="w-2.5 h-2.5" strokeWidth={2.5} />
              {recLabel}
            </span>
          )}
        </div>
        {reminder.description && (
          <p className="mt-1 text-[13px] text-slate-500 dark:text-slate-400 line-clamp-1">
            {reminder.description}
          </p>
        )}
        <div className="mt-2 flex items-center gap-3 text-[12px] text-slate-500 dark:text-slate-400 flex-wrap">
          <span className="inline-flex items-center gap-1.5">
            <CalendarIcon className="w-3.5 h-3.5" strokeWidth={2} />
            {dateLabel}
          </span>
          {time && (
            <>
              <span className="w-1 h-1 rounded-full bg-slate-300 dark:bg-white/20" />
              <span className="font-mono font-medium">{time}</span>
            </>
          )}
          {isOverdue && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-300 font-semibold">
              <AlertTriangle className="w-3 h-3" strokeWidth={2.5} /> Vencido
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex-shrink-0 flex items-center gap-1">
        <button
          data-testid={`reminder-edit-${reminder.id}`}
          onClick={() => onEdit(reminder)}
          aria-label="Editar"
          className="p-2 rounded-lg text-slate-400 hover:text-[#2563EB] hover:bg-[#2563EB]/5 dark:hover:bg-[#3B82F6]/10 transition-colors"
        >
          <Pencil className="w-4 h-4" strokeWidth={2} />
        </button>
        <div className="relative">
          <button
            data-testid={`reminder-menu-${reminder.id}`}
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Más acciones"
            className="p-2 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
          >
            <MoreVertical className="w-4 h-4" strokeWidth={2} />
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 mt-1 w-40 bg-white dark:bg-[#0B1220] border border-slate-200 dark:border-white/10 rounded-xl shadow-xl z-40 py-1">
                <button
                  data-testid={`reminder-menu-edit-${reminder.id}`}
                  onClick={() => { setMenuOpen(false); onEdit(reminder); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/5"
                >
                  <Pencil className="w-3.5 h-3.5" /> Editar
                </button>
                <button
                  data-testid={`reminder-menu-delete-${reminder.id}`}
                  onClick={() => { setMenuOpen(false); onDelete(reminder); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Eliminar
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ReminderModal({ open, initial, onClose, onSubmit }) {
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial;

  useEffect(() => {
    if (open) {
      setTitle(initial?.title || '');
      setDate(initial?.remind_at ? toDateTimeLocal(initial.remind_at) : '');
      setDescription(initial?.description || '');
    }
  }, [open, initial]);

  if (!open) return null;

  const handleSave = async (e) => {
    e.preventDefault();
    if (!title.trim() || !date) {
      toast.error('Poné un título y una fecha');
      return;
    }
    setSaving(true);
    try {
      await onSubmit({
        title: title.trim(),
        remind_at: date,
        description: description.trim() || null,
      });
      onClose();
    } catch (err) {
      // toast surfaced in caller
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-[#0B1220] rounded-2xl shadow-2xl max-w-md w-full border border-slate-200 dark:border-white/10"
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 dark:border-white/5">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">
            {isEdit ? 'Editar recordatorio' : 'Nuevo recordatorio'}
          </h2>
          <button
            data-testid="reminder-modal-close"
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/5"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSave} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-[13px] font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
              ¿Qué querés recordar?
            </label>
            <input
              data-testid="reminder-input-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
              placeholder="Ej: Pagar el alquiler"
              className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent transition-all"
            />
          </div>
          <div>
            <label className="block text-[13px] font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
              Fecha y hora
            </label>
            <input
              data-testid="reminder-input-date"
              type="datetime-local"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent transition-all"
            />
          </div>
          <div>
            <label className="block text-[13px] font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
              Notas <span className="font-normal text-slate-400">(opcional)</span>
            </label>
            <textarea
              data-testid="reminder-input-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Detalles extra..."
              className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent transition-all resize-none"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-200 font-semibold hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
            >
              Cancelar
            </button>
            <button
              data-testid="reminder-modal-save"
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2.5 rounded-xl bg-[#2563EB] text-white font-semibold hover:brightness-110 shadow-lg shadow-[#2563EB]/25 disabled:opacity-60 transition-all"
            >
              {saving ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------- main ----------
const FILTERS = [
  { key: 'todos', label: 'Todos' },
  { key: 'hoy', label: 'Hoy' },
  { key: 'manana', label: 'Mañana' },
  { key: 'proximos', label: 'Próximos' },
  { key: 'vencidos', label: 'Vencidos' },
];

export default function RemindersView() {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('todos');
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      // Usamos el MISMO endpoint que CalendarView: expande recurrencias en
      // ocurrencias reales dentro de un rango. Rango amplio: 60 días atrás
      // (para vencidos) → 2 años adelante (para futuras).
      const now = new Date();
      const from = new Date(now.getTime() - 60 * 86400000);
      const to = new Date(now.getTime() + 730 * 86400000);
      const fromParam = `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, '0')}-${String(from.getDate()).padStart(2, '0')}`;
      const toParam = `${to.getFullYear()}-${String(to.getMonth() + 1).padStart(2, '0')}-${String(to.getDate()).padStart(2, '0')}`;
      const res = await axios.get(
        `${API}/api/reminders/expanded?from=${fromParam}&to=${toParam}`,
        { withCredentials: true }
      );
      // Cada ocurrencia lleva `id` = id del template. Para navegación por
      // ocurrencia usamos una clave sintética (id + remind_at).
      const items = Array.isArray(res.data?.reminders) ? res.data.reminders : [];
      const withKey = items.map((r) => ({
        ...r,
        _key: `${r.id}::${r.remind_at}`,
        series_id: r.series_id || r.id,
      }));
      setReminders(withKey);
    } catch (err) {
      toast.error('No pudimos cargar tus recordatorios');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener('vida:data-changed', h);
    return () => window.removeEventListener('vida:data-changed', h);
  }, [load]);

  const now = useMemo(() => new Date(), [reminders.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const enriched = useMemo(() => {
    const q = search.trim().toLowerCase();
    return reminders
      .map((r) => ({ ...r, _bucket: bucketFor(r.remind_at, now), _d: parseAR(r.remind_at) }))
      .filter((r) => {
        if (!q) return true;
        return (r.title || '').toLowerCase().includes(q)
          || (r.description || '').toLowerCase().includes(q);
      })
      .sort((a, b) => {
        if (!a._d && !b._d) return 0;
        if (!a._d) return 1;
        if (!b._d) return -1;
        return a._d - b._d;
      });
  }, [reminders, search, now]);

  const counts = useMemo(() => ({
    todos: enriched.length,
    hoy: enriched.filter((r) => r._bucket === 'hoy').length,
    manana: enriched.filter((r) => r._bucket === 'manana').length,
    proximos: enriched.filter((r) => r._bucket === 'proximos').length,
    vencidos: enriched.filter((r) => r._bucket === 'vencidos').length,
  }), [enriched]);

  const filtered = useMemo(() => {
    if (filter === 'todos') return enriched;
    return enriched.filter((r) => r._bucket === filter);
  }, [enriched, filter]);

  const groups = useMemo(() => {
    // Sort by our custom bucket order: vencidos, hoy, manana, proximos
    const order = ['vencidos', 'hoy', 'manana', 'proximos'];
    const map = { vencidos: [], hoy: [], manana: [], proximos: [] };
    filtered.forEach((r) => { if (map[r._bucket]) map[r._bucket].push(r); });
    return order.filter((k) => map[k].length > 0).map((k) => ({ key: k, items: map[k] }));
  }, [filtered]);

  const handleCreate = async (payload) => {
    try {
      await axios.post(`${API}/api/reminders`, { ...payload, type: 'general' }, { withCredentials: true });
      toast.success('Recordatorio creado');
      load();
      window.dispatchEvent(new CustomEvent('vida:data-changed'));
    } catch (err) {
      toast.error('No pudimos crear el recordatorio');
      throw err;
    }
  };

  const handleUpdate = async (payload) => {
    try {
      const templateId = editing?.series_id || editing?.id;
      await axios.patch(`${API}/api/reminders/${templateId}`, payload, { withCredentials: true });
      toast.success('Recordatorio actualizado');
      load();
      window.dispatchEvent(new CustomEvent('vida:data-changed'));
    } catch (err) {
      toast.error('No pudimos actualizar el recordatorio');
      throw err;
    }
  };

  const handleDelete = async (reminder) => {
    try {
      // Ocurrencias comparten `id` (template). DELETE elimina la serie completa
      // igual que CalendarView. Filtramos por id para reflejar la baja de todas
      // las ocurrencias en pantalla al instante.
      const templateId = reminder.series_id || reminder.id;
      await axios.delete(`${API}/api/reminders/${templateId}`, { withCredentials: true });
      setReminders((prev) => prev.filter((r) => (r.series_id || r.id) !== templateId));
      toast.success('Recordatorio eliminado');
      window.dispatchEvent(new CustomEvent('vida:data-changed'));
    } catch (err) {
      toast.error('No pudimos eliminar el recordatorio');
    }
  };

  const handleComplete = handleDelete; // completar = eliminar (recurrentes eliminan la serie)

  const groupMeta = {
    vencidos: { title: 'Vencidos', icon: AlertTriangle, accent: 'text-red-600 dark:text-red-400' },
    hoy:      { title: 'Hoy',       icon: CalendarIcon,  accent: 'text-[#2563EB] dark:text-[#3B82F6]' },
    manana:   { title: 'Mañana',    icon: Sunrise,       accent: 'text-amber-600 dark:text-amber-400' },
    proximos: { title: 'Próximos',  icon: Bell,          accent: 'text-slate-600 dark:text-slate-300' },
  };

  const groupDateLabel = (items, key) => {
    if (key === 'vencidos' || key === 'proximos') return null;
    const d = items[0]?._d;
    return d ? humanDate(d) : null;
  };

  return (
    <div className="space-y-6" data-testid="reminders-view">
      {/* HEADER */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 dark:text-white leading-tight">
            Recordatorios
          </h1>
          <p className="mt-1 text-slate-500 dark:text-slate-400">
            Lo importante que no querés olvidar.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" strokeWidth={2} />
            <input
              data-testid="reminders-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar recordatorios"
              className="pl-9 pr-3 py-2.5 w-full sm:w-64 rounded-xl bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent transition-all"
            />
          </div>
          <button
            data-testid="reminders-new-btn"
            onClick={() => { setEditing(null); setModalOpen(true); }}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#2563EB] text-white text-sm font-semibold shadow-lg shadow-[#2563EB]/25 hover:brightness-110 transition-all whitespace-nowrap"
          >
            <Plus className="w-4 h-4" strokeWidth={2.5} />
            Nuevo recordatorio
          </button>
        </div>
      </div>

      {/* FILTERS */}
      <div className="-mx-4 sm:mx-0 px-4 sm:px-0">
        <div
          data-testid="reminders-filters"
          className="flex gap-2 overflow-x-auto pb-1"
          style={{ scrollbarWidth: 'none' }}
        >
          {FILTERS.map((f) => (
            <FilterChip
              key={f.key}
              testId={`reminders-filter-${f.key}`}
              label={f.label}
              count={counts[f.key]}
              active={filter === f.key}
              onClick={() => setFilter(f.key)}
            />
          ))}
        </div>
      </div>

      {/* LIST */}
      {loading ? (
        <div className="flex justify-center items-center py-16">
          <div className="w-10 h-10 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : groups.length === 0 ? (
        <div
          data-testid="reminders-empty"
          className="flex flex-col items-center justify-center py-16 px-6 bg-white dark:bg-[#0B1220] rounded-2xl border border-slate-200/70 dark:border-white/5"
        >
          <div className="w-16 h-16 rounded-2xl bg-[#2563EB]/10 dark:bg-[#3B82F6]/15 flex items-center justify-center mb-4">
            <Bell className="w-8 h-8 text-[#2563EB] dark:text-[#3B82F6]" strokeWidth={1.75} />
          </div>
          <h3 className="text-lg font-bold text-slate-900 dark:text-white">
            {search ? 'Nada coincide con tu búsqueda' : 'No tenés recordatorios acá'}
          </h3>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400 text-center max-w-sm">
            {search ? 'Probá con otra palabra.' : 'Creá el primero y te avisamos a tiempo.'}
          </p>
          {!search && (
            <button
              onClick={() => { setEditing(null); setModalOpen(true); }}
              className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#2563EB] text-white text-sm font-semibold shadow-lg shadow-[#2563EB]/25 hover:brightness-110 transition-all"
            >
              <Plus className="w-4 h-4" strokeWidth={2.5} />
              Nuevo recordatorio
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map(({ key, items }) => {
            const meta = groupMeta[key];
            const Icon = meta.icon;
            const dateLabel = groupDateLabel(items, key);
            return (
              <section key={key} data-testid={`reminders-group-${key}`}>
                <header className="flex items-center gap-2 mb-3 px-1">
                  <Icon className={`w-4 h-4 ${meta.accent}`} strokeWidth={2.5} />
                  <h2 className={`text-sm font-bold uppercase tracking-wider ${meta.accent}`}>
                    {meta.title}
                  </h2>
                  {dateLabel && (
                    <span className="text-xs text-slate-400 dark:text-slate-500 font-medium">
                      · {dateLabel}
                    </span>
                  )}
                  <span className="ml-auto text-xs font-semibold text-slate-400 dark:text-slate-500">
                    {items.length}
                  </span>
                </header>
                <div className="space-y-2.5">
                  {items.map((r) => (
                    <ReminderCard
                      key={r._key || r.id}
                      reminder={r}
                      isOverdue={key === 'vencidos'}
                      onComplete={handleComplete}
                      onEdit={(rem) => { setEditing(rem); setModalOpen(true); }}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      <ReminderModal
        open={modalOpen}
        initial={editing}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        onSubmit={editing ? handleUpdate : handleCreate}
      />
    </div>
  );
}
